import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Button from './Button';

const FilterSearchBar = ({ 
  onSearch, 
  onFilter, 
  searchPlaceholder = "Search...", 
  filters = [],
  showFilters = true,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, onSearch]);

  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...activeFilters };
    if (value === '' || value === null) {
      delete newFilters[filterKey];
    } else {
      newFilters[filterKey] = value;
    }
    setActiveFilters(newFilters);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchTerm('');
    if (onFilter) onFilter({});
    if (onSearch) onSearch('');
  };

  const getActiveFilterCount = () => {
    return Object.keys(activeFilters).length + (searchTerm ? 1 : 0);
  };

  const renderFilterContent = () => (
    <div className="space-y-4">
      {filters.map((filter) => (
        <div key={filter.key} className="space-y-2">
          <label className="block text-sm font-medium text-text-primary">
            {filter.label}
          </label>
          {filter.type === 'select' ? (
            <select
              className="filter-input w-full"
              value={activeFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            >
              <option value="">All {filter.label}</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : filter.type === 'date' ? (
            <Input
              type="date"
              value={activeFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="w-full"
            />
          ) : (
            <Input
              type="text"
              placeholder={filter.placeholder}
              value={activeFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="w-full"
            />
          )}
        </div>
      ))}
      
      <div className="flex space-x-2 pt-4 border-t border-border-light">
        <Button
          variant="outline"
          size="sm"
          onClick={clearAllFilters}
          className="flex-1"
        >
          Clear All
        </Button>
        {isMobile && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsFilterOpen(false)}
            className="flex-1"
          >
            Apply Filters
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon name="Search" size={16} className="text-text-secondary" />
          </div>
          <Input
            type="search"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>

        {/* Filter Toggle Button */}
        {showFilters && filters.length > 0 && (
          <Button
            variant="outline"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            iconName="Filter"
            iconPosition="left"
            className="relative"
          >
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {getActiveFilterCount()}
              </span>
            )}
          </Button>
        )}
      </div>

      {/* Desktop Filter Panel */}
      {!isMobile && isFilterOpen && showFilters && filters.length > 0 && (
        <div className="bg-surface border border-border rounded-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-text-primary">Filters</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFilterOpen(false)}
              iconName="X"
              iconSize={16}
            >
              <span className="sr-only">Close filters</span>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderFilterContent()}
          </div>
        </div>
      )}

      {/* Mobile Filter Modal */}
      {isMobile && isFilterOpen && showFilters && filters.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-300 flex items-end">
          <div className="bg-surface w-full max-h-[80vh] overflow-y-auto rounded-t-lg">
            <div className="sticky top-0 bg-surface border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-text-primary">Filters</h3>
              <Button
                variant="ghost"
                onClick={() => setIsFilterOpen(false)}
                iconName="X"
                iconSize={20}
              >
                <span className="sr-only">Close filters</span>
              </Button>
            </div>
            <div className="p-4">
              {renderFilterContent()}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <div className="flex flex-wrap gap-2">
          {searchTerm && (
            <div className="inline-flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
              <Icon name="Search" size={14} className="mr-1" />
              Search: "{searchTerm}"
              <button
                onClick={() => setSearchTerm('')}
                className="ml-2 hover:text-primary/70"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
          {Object.entries(activeFilters).map(([key, value]) => {
            const filter = filters.find(f => f.key === key);
            const displayValue = filter?.type === 'select' 
              ? filter.options.find(opt => opt.value === value)?.label || value
              : value;
            
            return (
              <div
                key={key}
                className="inline-flex items-center bg-accent/10 text-accent px-3 py-1 rounded-full text-sm"
              >
                {filter?.label}: {displayValue}
                <button
                  onClick={() => handleFilterChange(key, null)}
                  className="ml-2 hover:text-accent/70"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterSearchBar;